import { useState, useEffect } from 'react';
import { fetchPageBySlug, getResizedImageUrl } from '../lib/cms';
import ContactForm from '../components/ContactForm';
import { injectInternalLinks } from '../lib/utils';

export default function Kontakt() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadPage() {
      const data = await fetchPageBySlug('kontakt');
      setPageData(data);
      setLoading(false);
    }
    loadPage();
  }, []);

  useEffect(() => {
    if (pageData) {
      document.title = pageData.meta_title || 'Kontakt - Larsen & Kristensen';
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', pageData.meta_description || '');
      }
    }
  }, [pageData]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-[#163A2B] border-t-transparent"></div>
      </div>
    );
  }

  const heroBg = pageData?.hero_image?.[0] || 'https://assets.auxodev.dk/assets/54439585-0ea9-462f-a6c1-e69db40e10d7/gravearbejde-holstebro_5.webp';
  const resizedHeroBg = getResizedImageUrl(heroBg, 1600, 80);

  return (
    <div className="flex flex-col min-h-screen">
      
      {/* Page Header / Hero */}
      <section className="relative bg-[#1C1E1D] py-16 sm:py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={resizedHeroBg}
            alt="Kontakt Larsen & Kristensen"
            className="h-full w-full object-cover object-center opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#163A2B]/50 via-[#1C1E1D]/70 to-[#1C1E1D]/90"></div>
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#163A2B] bg-[#F4F1EA] px-3 py-1.5 rounded-full">
            Kontakt os
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mt-4 font-display">
            {pageData?.title || 'Kontakt'}
          </h1>
          <p className="mt-4 text-base sm:text-lg text-[#F4F1EA]/80 max-w-2xl mx-auto leading-relaxed">
            {pageData?.intro || 'Lad os tage en uforpligtende snak om dit næste projekt. Vi står klar til at hjælpe dig.'}
          </p>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Left Column: Contact Info & Details */}
            <div className="lg:col-span-5 space-y-8">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-[#1C1E1D] font-display">
                  Direkte kontaktoplysninger
                </h2>
                <div 
                  className="text-base text-[#1C1E1D]/80 mt-4 leading-relaxed space-y-4 prose prose-emerald"
                  dangerouslySetInnerHTML={{ __html: injectInternalLinks(pageData?.content_html, 'kontakt') }}
                />
              </div>

              {/* Contact Details Grid - NO hover effects on non-clickable items */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="bg-[#F4F1EA] border border-[#E2E2E0] rounded-lg p-6">
                  <div className="inline-flex items-center justify-center rounded-md bg-[#163A2B] text-white w-10 h-10 mb-4">
                    <i className="fa-solid fa-phone" />
                  </div>
                  <h3 className="text-xs text-[#1C1E1D]/50 uppercase font-bold">Ring direkte</h3>
                  <div className="mt-1.5 space-y-2">
                    <div>
                      <span className="text-xs text-[#1C1E1D]/60 block">Kristian:</span>
                      <a href="tel:+4530713311" className="text-base font-bold text-[#1C1E1D] hover:underline whitespace-nowrap">30 71 33 11</a>
                    </div>
                    <div>
                      <span className="text-xs text-[#1C1E1D]/60 block">Laurits:</span>
                      <a href="tel:+4591968419" className="text-base font-bold text-[#1C1E1D] hover:underline whitespace-nowrap">91 96 84 19</a>
                    </div>
                  </div>
                </div>

                <div className="bg-[#F4F1EA] border border-[#E2E2E0] rounded-lg p-6">
                  <div className="inline-flex items-center justify-center rounded-md bg-[#163A2B] text-white w-10 h-10 mb-4">
                    <i className="fa-solid fa-envelope" />
                  </div>
                  <h3 className="text-xs text-[#1C1E1D]/50 uppercase font-bold">Send en e-mail</h3>
                  <p className="text-base font-bold text-[#1C1E1D] mt-1 break-all">
                    <a href="mailto:kontakt@logkaps.dk" className="hover:underline">kontakt@logkaps.dk</a>
                  </p>
                </div>

                <div className="bg-[#F4F1EA] border border-[#E2E2E0] rounded-lg p-6">
                  <div className="inline-flex items-center justify-center rounded-md bg-[#163A2B] text-white w-10 h-10 mb-4">
                    <i className="fa-solid fa-location-dot" />
                  </div>
                  <h3 className="text-xs text-[#1C1E1D]/50 uppercase font-bold">Adresse</h3>
                  <p className="text-base font-bold text-[#1C1E1D] mt-1 whitespace-nowrap">
                    Krarupvej 58,<br />7800 Skive
                  </p>
                </div>

                <div className="bg-[#F4F1EA] border border-[#E2E2E0] rounded-lg p-6">
                  <div className="inline-flex items-center justify-center rounded-md bg-[#163A2B] text-white w-10 h-10 mb-4">
                    <i className="fa-solid fa-clock" />
                  </div>
                  <h3 className="text-xs text-[#1C1E1D]/50 uppercase font-bold">Åbningstider</h3>
                  <p className="text-sm font-bold text-[#1C1E1D] mt-1">
                    Man - Fre: 07:00 - 16:00<br />Lør - Søn: Lukket
                  </p>
                </div>
              </div>

              {/* Geographical Area Info Box - NO hover effects as it is not clickable */}
              <div className="bg-[#1C1E1D] text-white rounded-lg p-6 sm:p-8 border-l-4 border-[#163A2B]">
                <h3 className="text-lg font-bold font-display">Her kører vi</h3>
                <p className="text-sm text-[#F4F1EA]/80 mt-2 leading-relaxed">
                  Vi udfører opgaver for private, erhverv og offentlige kunder i hele Midt- og Vestjylland, herunder primært i:
                </p>
                <div className="mt-4 grid grid-cols-2 gap-2 text-sm font-semibold text-[#F4F1EA]">
                  <div className="flex items-center gap-2">
                    <i className="fa-solid fa-circle-check text-white" /> Skive & Salling
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="fa-solid fa-circle-check text-white" /> Viborg & omegn
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="fa-solid fa-circle-check text-white" /> Holstebro
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="fa-solid fa-circle-check text-white" /> Struer
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Contact Form */}
            <div className="lg:col-span-7">
              <ContactForm />
            </div>

          </div>
        </div>
      </section>

    </div>
  );
}
